import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import SupplierApiService from "@/core/services/api.service.supplier";

export default {
  name: "category-mapping",
  data() {
    return {
      // Filters
      filters: {
        showOnlyRoot: false,
        categoryName: "",
        selectedVendor: null,
        showUnmapped: false,
        showReadyToPublish: false,
        showWithUnpublishedProducts: false
      },

      // Table data
      categories: [],
      totalCategories: 0,
      loading: false,
      loadingCategories: false,
      pagination: {
        page: 1,
        itemsPerPage: 25
      },

      // Selection and bulk actions
      selectedItems: [],
      bulkMapping: null,
      bulkSaving: false,

      // Oobyte categories for mapping
      oobyteCategories: [],
      expandedCategories: new Set(), // Track expanded main categories
      originalCategories: [], // Store original categories for re-flattening
      categorySearchText: "",

      // Category filters
      categoryFilters: {
        vendors: []
      },
      loadingFilters: false,

      // Flag to prevent initial watcher trigger
      isInitialized: false
    };
  },

  mounted() {
    // Initialize API service
    SupplierApiService.init().then(() => {
      this.fetchOobyteCategories();
      // this.fetchCategories();
      this.fetchCategoryFilters();
    });

    // Add outside click listener for dropdowns
    document.addEventListener("click", this.handleOutsideClick);
  },

  beforeDestroy() {
    // Remove outside click listener
    document.removeEventListener("click", this.handleOutsideClick);
  },

  computed: {
    headers() {
      return [
        { text: "External Category", value: "name", width: "25%" },
        { text: "Vendor", value: "source", width: "15%" },
        { text: "Products Count", value: "countOfProducts", width: "15%" },
        { text: "Oobyte Category Mapping", value: "oobyteCategory", sortable: false, width: "35%" },
        { text: "Status", value: "mappingStatus", width: "15%" },
        { text: "Actions", value: "actions", sortable: false, width: "10%" }
      ];
    },

    tableHeight() {
      return window.innerHeight - 360;
    }
  },

  watch: {
    pagination: {
      handler() {
        SupplierApiService.init().then(() => {
          this.fetchCategories();
        });
      },
      deep: true
    }
  },

  async created() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Dashboard", route: "dashboard" },
      { title: "Category Mapping" }
    ]);
    this.isInitialized = true;
  },

  methods: {
    // Fetch external categories from real API
    async fetchCategories() {
      this.loading = true;

      try {
        const params = {
          page: this.pagination.page,
          maxResults: this.pagination.itemsPerPage,
          showOnlyRoot: this.filters.showOnlyRoot,
          showUnmapped: this.filters.showUnmapped,
          showReadyToPublish: this.filters.showReadyToPublish,
          showWithUnpublishedProducts: this.filters.showWithUnpublishedProducts,
          source: this.filters.selectedVendor,
          name: this.filters.categoryName
        };

        const response = await SupplierApiService.getSupplierCategories(params);

        // Handle API response structure
        if (response.data) {
          const categories = response.data.categories.results || [];

          // Add UI state properties to each category
          this.categories = categories.map(category => ({
            ...category,
            selectedMapping: Number(category.oobyteId) || null,
            saving: false,
            clearing: false,
            dropdownOpen: false,
            searchText: ""
          }));

          this.totalCategories = response.data.categories.metadata?.totalCount || 0;
        } else {
          this.categories = [];
          this.totalCategories = 0;
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        this.$toast?.error("Failed to fetch categories");

        // Fallback to empty state on error
        this.categories = [];
        this.totalCategories = 0;
      } finally {
        this.loading = false;
      }
    },

    // Fetch Oobyte categories from real API
    async fetchOobyteCategories() {
      this.loadingCategories = true;

      try {
        const response = await SupplierApiService.getOobyteCategories();

        if (response.data && Array.isArray(response.data)) {
          // Flatten categories and subcategories into a single searchable array
          this.oobyteCategories = this.flattenCategories(response.data);
          this.storeOriginalCategories(response.data);
        } else {
          console.error("Invalid response format for Oobyte categories");
          this.oobyteCategories = [];
        }
      } catch (error) {
        console.error("Error fetching Oobyte categories:", error);
        this.oobyteCategories = [];
      } finally {
        this.loadingCategories = false;
      }
    },

    async fetchCategoryFilters() {
      this.loadingFilters = true;

      try {
        const response = await SupplierApiService.getCategoryFilters();

        if (response.data && response.data.vendors) {
          this.categoryFilters.vendors = response.data.vendors;
        } else {
          console.warn("Invalid response format for category filters");
          this.categoryFilters.vendors = [];
        }
      } catch (error) {
        console.error("Error fetching category filters:", error);
        this.$toast?.error("Failed to fetch category filters");
        this.categoryFilters.vendors = [];
      } finally {
        this.loadingFilters = false;
      }
    },

    // Flatten categories into hierarchical structure for dropdown
    flattenCategories(categories, isFullFlatten = false) {
      const flattened = [];

      categories.forEach(category => {
        // Level 0 - Main category (now selectable)
        flattened.push({
          id: category.id, // Use original ID for selection
          title: category.title,
          displayName: category.title,
          level: 0,
          selectable: true, // Allow selection
          originalId: category.id
        });

        // Level 1 - Subcategories
        if (category.subcategories && category.subcategories.length > 0) {
          category.subcategories.forEach(subcategory => {
            const hasSubSubcategories =
              subcategory.subcategories && subcategory.subcategories.length > 0;
            const isExpanded = this.expandedCategories.has(subcategory.id);

            flattened.push({
              id: subcategory.id,
              title: subcategory.title,
              displayName: subcategory.title,
              level: 1,
              selectable: true, // Allow selecting level 1 categories even with subcategories
              parentId: category.id,
              parentTitle: category.title,
              originalId: subcategory.id,
              hasSubcategories: hasSubSubcategories,
              expanded: isExpanded
            });

            // Level 2 - Sub-subcategories (always selectable, only show if parent is expanded)
            if ((hasSubSubcategories && isExpanded) || isFullFlatten) {
              subcategory.subcategories.forEach(subSubcategory => {
                flattened.push({
                  id: subSubcategory.id,
                  title: subSubcategory.title,
                  displayName: subSubcategory.title,
                  level: 2,
                  selectable: true,
                  parentId: subcategory.id,
                  parentTitle: subcategory.title,
                  grandParentId: category.id,
                  grandParentTitle: category.title,
                  originalId: subSubcategory.id
                });
              });
            }
          });
        }
      });

      return flattened;
    },

    // Check if category is selected (for checkbox state)
    isCategorySelected(categoryId) {
      return this.filters.categories.includes(categoryId);
    },

    // Handle custom dropdown item click
    handleCustomDropdownClick(item, categoryItem) {
      // Always prevent selection for non-selectable items
      if (!categoryItem.selectable) {
        // If it's a level-1 category with subcategories, toggle expansion
        if (categoryItem.level === 1 && categoryItem.hasSubcategories) {
          this.toggleCategoryExpansion(categoryItem.id);
        }
        return;
      }

      // For selectable items, set the selection and close dropdown
      item.selectedMapping = categoryItem.id;
      item.dropdownOpen = false;
      item.searchText = "";
    },

    // Get hierarchical categories for filters with search support
    getHierarchicalCategoriesForFilters() {
      if (!this.originalCategories || this.originalCategories.length === 0) {
        return [];
      }

      const searchText = this.categorySearchText || "";

      if (!searchText.trim()) {
        // Clear all expanded categories when search is cleared to show only level 1 categories
        // this.expandedCategories.clear();
        // Return normal flattened categories (all collapsed)
        return this.flattenCategories(this.originalCategories);
      }

      // For search, get all categories including nested ones (full flatten)
      const allCategories = this.flattenCategories(this.originalCategories, true);

      // Filter categories based on search text
      const searchLower = searchText.toLowerCase();
      const filteredCategories = [];
      const addedHeaders = new Set();

      allCategories.forEach(category => {
        if (category.level === 0) {
          // Always check if we need this header
          return;
        }

        // Check if category matches search
        const matches = category.title.toLowerCase().includes(searchLower);

        if (matches) {
          // Add parent header if not already added
          const headerKey =
            category.level === 1
              ? `header_${category.parentId}`
              : `header_${category.grandParentId}`;

          if (!addedHeaders.has(headerKey)) {
            const headerCategory = allCategories.find(cat => cat.id === headerKey);
            if (headerCategory) {
              filteredCategories.push(headerCategory);
              addedHeaders.add(headerKey);
            }
          }

          // Add intermediate parent for level 2 items and expand it
          if (category.level === 2) {
            const parentKey = category.parentId;
            const parentExists = filteredCategories.some(cat => cat.id === parentKey);
            if (!parentExists) {
              const parentCategory = allCategories.find(cat => cat.id === parentKey);
              if (parentCategory) {
                // Expand the parent when showing search results
                parentCategory.expanded = true;
                this.expandedCategories.add(parentCategory.id);
                filteredCategories.push(parentCategory);
              }
            }
          }

          filteredCategories.push(category);
        }
      });

      return filteredCategories;
    },

    // Get display name for selected category
    getSelectedCategoryDisplayName(item) {
      if (!item.selectedMapping) return "";

      const selectedCategory = this.flattenCategories(this.originalCategories || [], true).find(
        cat => cat.id === item.selectedMapping
      );

      if (!selectedCategory) return "";

      return selectedCategory.title;
    },

    // Toggle category expansion
    toggleCategoryExpansion(categoryId) {
      if (this.expandedCategories.has(categoryId)) {
        this.expandedCategories.delete(categoryId);
      } else {
        this.expandedCategories.add(categoryId);
      }

      // Force update to refresh all dropdowns
      this.$forceUpdate();
    },

    // Store original categories for re-flattening
    storeOriginalCategories(categories) {
      this.originalCategories = categories;
    },

    // Apply filters and refresh data
    applyFilters() {
      this.pagination.page = 1;
      this.fetchCategories();
    },

    // Clear name filter
    clearNameFilter() {
      this.filters.categoryName = "";
      this.pagination.page = 1;
      this.fetchCategories();
    },

    // Clear all filters
    clearFilters() {
      this.filters = {
        showOnlyRoot: false,
        categoryName: "",
        selectedVendor: null,
        showUnmapped: false,
        showReadyToPublish: false,
        showWithUnpublishedProducts: false
      };
      this.pagination.page = 1;
      this.fetchCategories();
    },

    // Handle category mapping selection change
    onCategoryMappingChange(category) {
      // Find the selected Oobyte category to get parent info
      const selectedCategory = this.oobyteCategories.find(
        cat => cat.id === category.selectedMapping
      );
      if (selectedCategory) {
        // Find parent category ID if it has a parent
        const parentCategory = selectedCategory
          ? this.oobyteCategories.find(cat => cat.name === selectedCategory.parentName)
          : null;

        category.selectedParentId = parentCategory ? parentCategory.id : null;
      }
    },

    // Save individual category mapping using real API
    async saveMapping(category) {
      category.saving = true;

      try {
        const selectedCategory = this.oobyteCategories.find(
          cat => cat.id === category.selectedMapping
        );
        const parentCategory = selectedCategory
          ? this.oobyteCategories.find(cat => cat.name === selectedCategory.parentName)
          : null;

        const mappingData = {
          id: category.id,
          oobyteId: category.selectedMapping.toString(),
          oobyteParentId: parentCategory
            ? parentCategory.id.toString().replace(/^header_/, "")
            : null
        };

        const response = await SupplierApiService.updateSupplierCategoryMapping(mappingData);

        // Update current display
        category.oobyteId = category.selectedMapping;
        category.oobyteParentId = parentCategory ? parentCategory.id : null;

        this.$bvToast?.toast("Category mapping saved successfully", {
          variant: "success",
          solid: true,
          autoHideDelay: 2000
        });

        // setTimeout(async () => {
        //   // Trigger automatic products publishing for the mapped category
        //   await this.initiateProductsPublishing(category.id);
        // }, 2500);
      } catch (error) {
        console.error("Error saving category mapping:", error);
        this.$bvToast?.toast("Failed to save category mapping", {
          variant: "danger",
          solid: true,
          autoHideDelay: 2000
        });

        // Revert the selection on error
        category.selectedMapping = category.oobyteId;
      } finally {
        category.saving = false;
      }
    },

    // Initiate automatic products publishing for a category
    // async initiateProductsPublishing(categoryId) {
    //   try {
    //     await SupplierApiService.automaticProductsPublishing(categoryId);
    //     this.$bvToast?.toast("Products publishing successfully initiated for this category", {
    //       variant: "success",
    //       solid: true,
    //       autoHideDelay: 2000
    //     });
    //   } catch (publishError) {
    //     console.error("Error in automatic products publishing:", publishError);
    //     this.$bvToast?.toast(
    //       "Category mapped successfully, but failed to initiate products publishing",
    //       {
    //         variant: "warning",
    //         solid: true,
    //         autoHideDelay: 2000
    //       }
    //     );
    //   }
    // },

    // Clear category selection
    clearSelection(item) {
      item.selectedMapping = null;
      item.dropdownOpen = false;
      this.onCategoryMappingChange(item);
    },

    // Handle category click
    handleCategoryClick(item, categoryItem, event) {
      // Always prevent selection for non-selectable items

      if (!categoryItem.selectable) {
        event.stopPropagation();
        event.preventDefault();

        // If it's a level-1 category with subcategories, toggle expansion
        if (categoryItem.level === 1 && categoryItem.hasSubcategories) {
          this.toggleCategoryExpansion(categoryItem.id);
        }
        return;
      }
    },

    // Toggle dropdown open/close
    toggleDropdown(item) {
      // If dropdown is closed, open it to show selected category
      if (!item.dropdownOpen) {
        item.dropdownOpen = true;

        // If user has selected a level 2 category, auto-expand its parent
        if (item.selectedMapping) {
          const selectedCategory = this.flattenCategories(this.originalCategories || [], true).find(
            cat => cat.id === item.selectedMapping
          );

          // If selected category is level 2, expand its parent
          if (selectedCategory && selectedCategory.level === 2) {
            this.expandedCategories.add(selectedCategory.parentId);
          }
        }
      } else {
        // If already open, close it
        item.dropdownOpen = false;
      }

      // Close other dropdowns
      this.categories.forEach(category => {
        if (category !== item) {
          category.dropdownOpen = false;
        }
      });
    },

    // Handle custom dropdown item click
    handleCustomDropdownClick(item, categoryItem) {
      // Always prevent selection for non-selectable items
      if (!categoryItem.selectable) {
        // If it's a level-1 category with subcategories, toggle expansion
        if (categoryItem.level === 1 && categoryItem.hasSubcategories) {
          this.toggleCategoryExpansion(categoryItem.id);
          // Force update for this specific item's dropdown
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        }
        return;
      }

      // For selectable items, set the selection and close dropdown
      item.selectedMapping = categoryItem.id;
      item.dropdownOpen = false;
      item.searchText = "";
    },

    // Handle outside click to close dropdowns
    handleOutsideClick(event) {
      const dropdowns = Array.from(this.$el.querySelectorAll(".custom-dropdown-menu"));
      const target = event.target;

      // Check if click is on a text field or its children (input, icons, etc.)
      const isTextFieldClick =
        target.closest(".v-text-field") ||
        target.closest(".custom-select-wrapper") ||
        target.classList.contains("v-input") ||
        target.classList.contains("v-text-field") ||
        target.classList.contains("custom-dropdown-item");

      // If click is on text field or dropdown trigger, return early
      if (isTextFieldClick) {
        return;
      }

      // Check if click is outside any dropdown
      if (!dropdowns.some(dropdown => dropdown.contains(target))) {
        // Close all dropdowns
        this.categories.forEach(category => {
          category.dropdownOpen = false;
        });
      }
    }
  }
};
